export function timeAgo(iso?: string | null): string {
  if (!iso) return ''
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000))
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  return `${Math.floor(hr / 24)}d ago`
}

// Breadcrumb for the Readiness header (I5 §2, §4): `Dashboard › …` with no
// module segment; detail pages follow the origin trail. `trail` is the origin's
// crumbs after Dashboard (empty when the origin is the Dashboard itself).
const FEATURE_ROOTS = ['progress', 'submission', 'tracker', 'catalogue', 'objectives', 'scoring', 'report', 'export', 'assessments', 'snapshots', 'migration', 'settings']
export function readinessCrumbs(pathname: string, opts: { indexId: string; trail: Array<{ label: string; to: string }>; techName?: (id: string) => string | null; snapshotName?: (id: string) => string | null }): Array<{ label: string; to?: string }> {
  const q = `?index=${opts.indexId}`
  const crumbs: Array<{ label: string; to?: string }> = [{ label: 'Dashboard', to: `/readiness${q}` }]
  const m = pathname.match(/^\/readiness\/(?:objectives\/)?([^/]+)\/?$/)
  if (m && (pathname.startsWith('/readiness/objectives/') || !FEATURE_ROOTS.includes(m[1]))) {
    crumbs.push(...opts.trail, { label: decodeURIComponent(m[1]) })
  } else if (pathname.startsWith('/readiness/progress')) {
    crumbs.push({ label: 'Progress', to: `/readiness/progress${q}` })
    if (pathname.startsWith('/readiness/progress/print')) crumbs.push({ label: 'Progress Report' })
  } else if (pathname.startsWith('/readiness/scoring')) crumbs.push({ label: 'How this is scored' })
  else if (pathname.startsWith('/readiness/submission')) {
    crumbs.push({ label: 'CSA Submission', to: `/readiness/submission${q}` })
    if (pathname.startsWith('/readiness/submission/versions')) crumbs.push({ label: 'Plan version' })
    else if (pathname.startsWith('/readiness/submission/plan')) crumbs.push({ label: 'Submission plan' })
  } else if (pathname.startsWith('/readiness/tracker')) {
    crumbs.push({ label: 'Migration Tracker', to: `/readiness/tracker${q}` })
    crumbs.push({ label: pathname.startsWith('/readiness/tracker/roadmap') ? 'Roadmap' : 'Migration items' })
  } else if (pathname.match(/^\/readiness\/catalogue\/[^/]+/)) {
    const t = pathname.match(/^\/readiness\/catalogue\/([^/]+)/)!
    crumbs.push(...opts.trail, { label: opts.techName?.(decodeURIComponent(t[1])) ?? 'Technology' })
  } else if (pathname.startsWith('/readiness/catalogue')) {
    crumbs.push({ label: 'Technology Catalogue', to: `/readiness/catalogue${q}` })
  } else if (/^\/readiness\/report\/live\/?$/.test(pathname)) crumbs.push(...opts.trail, { label: 'Summary Report' })
  else if (pathname.startsWith('/readiness/report/')) {
    const id = pathname.split('/')[3] ?? ''
    crumbs.push(...opts.trail, { label: opts.snapshotName?.(id) ?? 'Readiness Report' })
  }
  return crumbs
}

// Browser tab title on Readiness routes (I4 §11, I5 §2 and §5, I6 §11.2):
// `Quantizant Readiness · {page}`, where {page} is the feature, the technology
// name, the objective code or a report's title-cased name.
export function readinessPageTitle(pathname: string, techName?: (id: string) => string | null): string {
  const page = (() => {
    const t = pathname.match(/^\/readiness\/catalogue\/([^/]+)/)
    if (t) return techName?.(decodeURIComponent(t[1])) ?? 'Technology Catalogue'
    if (pathname.startsWith('/readiness/catalogue')) return 'Technology Catalogue'
    if (pathname.startsWith('/readiness/tracker')) return 'Migration Tracker'
    if (pathname.startsWith('/readiness/submission')) return 'CSA Submission'
    if (pathname.startsWith('/readiness/progress/print')) return 'Progress Report'
    if (pathname.startsWith('/readiness/progress') || pathname.startsWith('/readiness/assessments')) return 'Progress'
    if (/^\/readiness\/report\/live\/?$/.test(pathname)) return 'Summary Report'
    if (pathname.startsWith('/readiness/report/')) return 'Readiness Report'
    const m = pathname.match(/^\/readiness\/(?:objectives\/)?([^/]+)\/?$/)
    if (m && (pathname.startsWith('/readiness/objectives/') || !FEATURE_ROOTS.includes(m[1]))) return decodeURIComponent(m[1])
    return 'Dashboard'
  })()
  return `Quantizant Readiness · ${page}`
}

// Short unique id for in-browser records (prefix + time + random).
export const uid = (prefix: string) =>
  `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
